import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { toast } from "sonner";
import {
  Sparkles,
  CheckCircle2,
  Loader2,
  CalendarDays,
  Clock,
  User,
  Mail,
  Phone,
  Wrench,
  MessageSquare,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import Reveal from "@/components/common/Reveal";

const SERVICES = [
  "AI Customer Support Chatbot",
  "WhatsApp Automation",
  "Lead Generation & CRM Setup",
  "AI Sales Assistant",
  "Business Process Automation",
  "Appointment Booking System",
  "AI-Powered Analytics Dashboard",
  "Custom AI Solution",
  "Other",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  service: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

function validate(form) {
  const errs = {};
  if (!form.name.trim()) errs.name = "Full name is required.";
  if (!form.phone.trim()) errs.phone = "Phone number is required.";
  else if (form.phone.replace(/\D/g, "").length < 7)
    errs.phone = "Enter a valid phone number.";
  if (!form.email.trim()) errs.email = "Email address is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    errs.email = "Enter a valid email address.";
  if (!form.service) errs.service = "Please select a service.";
  if (!form.preferred_date) errs.preferred_date = "Preferred date is required.";
  if (!form.preferred_time) errs.preferred_time = "Preferred time is required.";
  return errs;
}

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const iconRef = useRef(null);

  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm({ ...form, [k]: val });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from("bookings").insert({
        id: crypto.randomUUID(),
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        message: form.message,
      });
      if (error) throw error;
      setShowSuccess(true);
      setForm(initial);
      setErrors({});
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showSuccess || !iconRef.current) return;
    gsap.fromTo(
      iconRef.current,
      { scale: 0, rotate: -15 },
      { scale: 1, rotate: 0, duration: 0.6, ease: "back.out(1.7)" }
    );
  }, [showSuccess]);

  return (
    <>
      <section id="booking" className="section relative" data-testid="booking-section">
        <div className="absolute inset-0 aurora opacity-60" />
        <div className="container-x relative">
          <div className="grid lg:grid-cols-12 gap-10">
            <Reveal direction="up" className="lg:col-span-5">
              <div>
                <span className="eyebrow">
                  <span className="dot" /> Book a call
                </span>
                <h2 className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]">
                  Let's design your{" "}
                  <span className="font-serif italic gradient-text">AI workforce</span>.
                </h2>
                <p className="mt-5 text-[var(--body-fg)]/65 leading-relaxed">
                  30 minutes. Zero pressure. You'll walk away with a clear AI roadmap
                  tailored to your business — even if we don't end up working together.
                </p>

                <ul className="mt-8 space-y-3 text-sm text-[var(--body-fg)]/75">
                  {[
                    "We study your business before the call",
                    "Founder-led conversation — no sales reps",
                    "Walk away with a tailored AI strategy",
                    "No obligation, no follow-up spam",
                  ].map((x) => (
                    <li key={x} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-[var(--brand)] mt-0.5" /> {x}
                    </li>
                  ))}
                </ul>

                <div className="mt-10 glass glow-border rounded-2xl p-4 flex items-center gap-4 tilt-card">
                  <div className="avatar-orb h-10 w-10 text-sm font-bold">M</div>
                  <div className="text-sm">
                    <div className="text-[var(--body-fg)]">Mudit Pratap Singh</div>
                    <div className="text-[var(--body-fg)]/55 text-xs">
                      Founder — will personally take your call
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal direction="up" delay={0.1} className="lg:col-span-7">
              <form
                onSubmit={submit}
                className="glass-strong glow-border rounded-3xl p-6 sm:p-8"
                style={{ boxShadow: "0 30px 70px -30px var(--brand-glow)" }}
                data-testid="booking-form"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name *" error={errors.name} icon={<User className="w-3.5 h-3.5" />}>
                    <Input
                      value={form.name}
                      onChange={set("name")}
                      placeholder="e.g. Aman Sharma"
                      data-testid="bk-name-input"
                      className={inputCls(errors.name)}
                    />
                  </Field>

                  <Field label="Phone Number *" error={errors.phone} icon={<Phone className="w-3.5 h-3.5" />}>
                    <Input
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 9XXXXXXXXX"
                      data-testid="bk-phone-input"
                      className={inputCls(errors.phone)}
                    />
                  </Field>

                  <Field label="Email Address *" error={errors.email} icon={<Mail className="w-3.5 h-3.5" />}>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      data-testid="bk-email-input"
                      className={inputCls(errors.email)}
                    />
                  </Field>

                  <Field label="Selected Service *" error={errors.service} icon={<Wrench className="w-3.5 h-3.5" />}>
                    <Select value={form.service} onValueChange={(v) => set("service")(v)}>
                      <SelectTrigger
                        data-testid="bk-service-trigger"
                        className={inputCls(errors.service)}
                      >
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--body-bg)] border-[var(--glass-border)] text-[var(--body-fg)]">
                        {SERVICES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Preferred Date *" error={errors.preferred_date} icon={<CalendarDays className="w-3.5 h-3.5" />}>
                    <Input
                      type="date"
                      value={form.preferred_date}
                      onChange={set("preferred_date")}
                      data-testid="bk-date-input"
                      className={inputCls(errors.preferred_date)}
                    />
                  </Field>

                  <Field label="Preferred Time *" error={errors.preferred_time} icon={<Clock className="w-3.5 h-3.5" />}>
                    <Input
                      type="time"
                      value={form.preferred_time}
                      onChange={set("preferred_time")}
                      data-testid="bk-time-input"
                      className={inputCls(errors.preferred_time)}
                    />
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="Message / Requirements" error={errors.message} icon={<MessageSquare className="w-3.5 h-3.5" />}>
                    <Textarea
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your project, goals, or any questions you have..."
                      data-testid="bk-message-input"
                      className="bg-[var(--body-fg)]/[0.03] border-[var(--glass-border)] text-[var(--body-fg)] placeholder:text-[var(--body-fg)]/30 min-h-[100px]"
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit-btn"
                  className="btn-primary mt-7 w-full justify-center disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Book Free AI Consultation
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-[var(--body-fg)]/40 mt-3">
                  We respect your privacy. We never share or sell your data.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md bg-[var(--body-bg)] border border-[var(--glass-border)] text-[var(--body-fg)] rounded-2xl p-8 text-center">
          <div className="flex flex-col items-center">
            <div
              ref={iconRef}
              className="inline-flex h-16 w-16 items-center justify-center rounded-full mb-5"
              style={{ background: "var(--eyebrow-bg)", border: "1px solid var(--eyebrow-border)" }}
            >
              <CheckCircle2 className="w-8 h-8 text-[var(--brand)]" />
            </div>
            <DialogTitle className="text-2xl font-semibold text-[var(--body-fg)]">
              Thank you!
            </DialogTitle>
            <DialogDescription className="text-[var(--body-fg)]/65 mt-3 text-base leading-relaxed max-w-sm">
              Your booking request has been received. We will contact you shortly.
            </DialogDescription>
            <button
              onClick={() => setShowSuccess(false)}
              className="btn-secondary mt-7"
              data-testid="booking-close-success"
            >
              Got it, thanks!
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const BASE_INPUT = "bg-[var(--body-fg)]/[0.03] border-[var(--glass-border)] text-[var(--body-fg)] placeholder:text-[var(--body-fg)]/30 ";
function inputCls(error) {
  return `${BASE_INPUT}${error ? " !border-red-500/60 focus-visible:ring-red-500/40" : ""}`;
}

function Field({ label, error, icon, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[var(--body-fg)]/45 mb-2">
        {icon}
        {label}
      </div>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-400/90" data-testid="field-error">
          {error}
        </p>
      )}
    </label>
  );
}
